import React, { useState, useEffect } from 'react';
import { getDrivers, addDriver, updateDriver, deleteDriver } from '../firebase/database';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { initializeApp, getApps } from 'firebase/app';

// Secondary Firebase app — creates driver accounts without signing out the admin
const getSecondaryAuth = () => {
  const secondaryApp = getApps().find(a => a.name === 'secondary') ||
    initializeApp(getApps()[0].options, 'secondary');
  return getAuth(secondaryApp);
};

// Writes the Firestore driver doc given a known UID
const writeDriverDoc = async (uid, form) => {
  await setDoc(doc(db, 'users', uid), {
    uid,
    name: form.name,
    email: form.email,
    phone: form.phone,
    vehicleNumber: form.vehicleNumber,
    vehicleType: 'Car',
    role: 'driver',
    isActive: true,
    fcmToken: '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

const EMPTY_FORM = {
  name: '', email: '', phone: '', password: '',
  vehicleNumber: '', vehicleType: 'Car', isActive: true
};

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      const data = await getDrivers();
      setDrivers(data);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingDriver(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (driver) => {
    setEditingDriver(driver);
    setForm({ ...driver, password: '' });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (editingDriver) {
        const { password, ...data } = form;
        await updateDriver(editingDriver.id, { ...data, vehicleType: 'Car' });
      } else {
        const secondaryAuth = getSecondaryAuth();
        let uid;

        try {
          const { user } = await createUserWithEmailAndPassword(secondaryAuth, form.email, form.password);
          uid = user.uid;
          await secondaryAuth.signOut();
        } catch (authErr) {
          if (authErr.code === 'auth/email-already-in-use') {
            const { signInWithEmailAndPassword } = await import('firebase/auth');
            const { user } = await signInWithEmailAndPassword(secondaryAuth, form.email, form.password);
            uid = user.uid;
            await secondaryAuth.signOut();
            const existing = await getDoc(doc(db, 'users', uid));
            if (existing.exists()) {
              setError('A driver with this email already exists.');
              setSaving(false);
              return;
            }
          } else {
            throw authErr;
          }
        }
        await writeDriverDoc(uid, form);
      }
      await loadDrivers();
      setShowModal(false);
    } catch (err) {
      setError(err.message || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (driver) => {
    await updateDriver(driver.id, { isActive: !driver.isActive });
    await loadDrivers();
  };

  const handleDelete = async (driver) => {
    if (!window.confirm(`Delete driver "${driver.name}"? This cannot be undone.`)) return;
    await deleteDriver(driver.id);
    await loadDrivers();
  };

  const filtered = drivers.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.phone?.includes(search) ||
    d.vehicleNumber?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>{drivers.length} total drivers</div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <input
            placeholder="Search by name, phone, vehicle..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '9px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
              fontSize: 13, outline: 'none', width: 260
            }}
          />
          <button onClick={openAddModal} style={{
            padding: '9px 18px', background: '#f59e0b', border: 'none',
            borderRadius: 8, color: '#0f172a', fontWeight: 700, fontSize: 14, cursor: 'pointer'
          }}>
            + Add Driver
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading drivers...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Driver', 'Phone', 'Vehicle', 'Type', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 13 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8' }}>
                    {search ? 'No drivers match your search' : 'No drivers added yet. Click "+ Add Driver" to get started.'}
                  </td>
                </tr>
              )}
              {filtered.map((driver, i) => (
                <tr key={driver.id} style={{
                  borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none'
                }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', background: '#dbeafe',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 14, color: '#1e40af', flexShrink: 0
                      }}>
                        {driver.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{driver.name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{driver.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>{driver.phone}</td>
                  <td style={{ padding: '12px 16px', color: '#475569', fontWeight: 600 }}>{driver.vehicleNumber}</td>
                  <td style={{ padding: '12px 16px', color: '#475569' }}>{driver.vehicleType}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: driver.isActive ? '#dcfce7' : '#fee2e2',
                      color: driver.isActive ? '#166534' : '#991b1b'
                    }}>
                      {driver.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn onClick={() => openEditModal(driver)} color="#3b82f6" label="Edit" />
                      <Btn onClick={() => handleToggleActive(driver)} color={driver.isActive ? '#f59e0b' : '#22c55e'}
                        label={driver.isActive ? 'Deactivate' : 'Activate'} />
                      <Btn onClick={() => handleDelete(driver)} color="#ef4444" label="Delete" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <Modal title={editingDriver ? 'Edit Driver' : 'Add New Driver'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave} autoComplete="off">
            {/* Honeypot fields to prevent autofill */}
            <input type="text" style={{ display: 'none' }} autoComplete="username" readOnly />
            <input type="password" style={{ display: 'none' }} autoComplete="current-password" readOnly />
            <Field label="Full Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required autoComplete="off" />
            <Field label="Email" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} required disabled={!!editingDriver} autoComplete="off" />
            {!editingDriver && (
              <Field label="Password (for driver login)" type="password" value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} required minLength={6} autoComplete="new-password" />
            )}
            {/* US Phone Field */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>
                Phone Number *
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{
                  padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8,
                  fontSize: 14, background: '#f9fafb', color: '#374151', fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>🇺🇸 +1</div>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = digits;
                    if (digits.length > 3 && digits.length <= 6) formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                    else if (digits.length > 6) formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                    setForm(f => ({ ...f, phone: formatted }));
                  }}
                  placeholder="(555) 555-5555"
                  required
                  autoComplete="off"
                  style={{
                    flex: 1, padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
            <Field label="Vehicle Number" value={form.vehicleNumber} onChange={v => setForm(f => ({ ...f, vehicleNumber: v }))} required />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>
                Vehicle Type
              </label>
              <input
                value="Car"
                disabled
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: 14, background: '#f9fafb', color: '#374151',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button type="submit" disabled={saving} style={{ padding: '10px 20px', background: '#f59e0b', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                {saving ? 'Saving...' : (editingDriver ? 'Update Driver' : 'Create Driver')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Btn({ onClick, color, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 10px', background: `${color}18`, border: `1px solid ${color}40`,
      borderRadius: 6, color, fontSize: 12, fontWeight: 600, cursor: 'pointer'
    }}>{label}</button>
  );
}

function Field({ label, value, onChange, type = 'text', required, disabled, minLength, autoComplete }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6, fontWeight: 500 }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        minLength={minLength}
        autoComplete={autoComplete || 'off'}
        style={{
          width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8,
          fontSize: 14, outline: 'none', boxSizing: 'border-box',
          background: disabled ? '#f9fafb' : '#fff', color: '#111827'
        }}
      />
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20
    }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: '#fff'
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</div>
          <button onClick={onClose} style={{
            width: 32, height: 32, border: 'none', background: '#f1f5f9',
            borderRadius: '50%', cursor: 'pointer', fontSize: 18, color: '#64748b',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>×</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
